import type { FC, FocusEvent, KeyboardEvent, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { initials } from '@/lib/format'
import { useInView } from '@/hooks/use-in-view'
import { useReducedMotion } from '@/hooks/use-reduced-motion'
import { Reveal } from '@/components/generals/reveal'
import { LiveDot } from '@/components/generals/live-dot'
import { CreamSheet } from '@/components/generals/dark-band'
import { RollingNumber } from '@/components/generals/rolling-number'
import { SectionHeading } from '@/components/generals/section-heading'
import { PartnerWordmark } from '@/components/home/partner-wordmark'
import { customerStories } from '@/constants/home'

const STORY_DURATION = 7000

/**
 * Customer stories as an arrivals board: pick a customer on the board and
 * their quote and headline result fill the panel. The board advances on its
 * own while it is on screen, and pauses on hover or keyboard focus.
 */
export const Testimonials: FC = (): ReactNode => {
    const stageRef = useRef<HTMLDivElement>(null)
    const tabRefs = useRef<Array<HTMLButtonElement | null>>([])
    const listRef = useRef<HTMLDivElement>(null)
    const inView = useInView(stageRef, { threshold: 0.3 })
    const reduced = useReducedMotion()
    const [active, setActive] = useState(0)
    const [cycle, setCycle] = useState(0)
    const [paused, setPaused] = useState(false)
    const story = customerStories[active]
    const total = customerStories.length
    const running = inView && !paused

    useEffect(() => {
        const list = listRef.current
        const tab = tabRefs.current[active]

        if (!list || !tab || list.scrollWidth <= list.clientWidth) return

        list.scrollTo({ left: tab.offsetLeft - 8, behavior: reduced ? 'auto' : 'smooth' })
    }, [active, reduced])

    const select = (index: number) => {
        setActive(index)
        setCycle(value => value + 1)
    }

    const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
        let next = -1

        if (event.key === 'ArrowDown' || event.key === 'ArrowRight') next = (index + 1) % total
        if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') next = (index - 1 + total) % total
        if (event.key === 'Home') next = 0
        if (event.key === 'End') next = total - 1

        if (next < 0) return

        event.preventDefault()
        select(next)
        tabRefs.current[next]?.focus()
    }

    const handleBlur = (event: FocusEvent<HTMLDivElement>) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setPaused(false)
    }

    return (
        <CreamSheet id='customer-stories' aria-labelledby='customer-stories-title' className='section-y'>
            <div className='shell'>
                <SectionHeading
                    id='customer-stories-title'
                    eyebrow="Customer stories"
                    title="Word from"
                    accent="the quay."
                    intro="Six teams on six lanes, from food distribution to bikes. Pick a line on the board to hear it from them."
                />

                <div
                    ref={stageRef}
                    className='mt-12 grid grid-cols-1 gap-4 *:min-w-0 lg:mt-16 lg:grid-cols-12 lg:gap-5'
                    onMouseEnter={() => setPaused(true)}
                    onMouseLeave={() => setPaused(false)}
                    onFocus={() => setPaused(true)}
                    onBlur={handleBlur}
                >
                    <Reveal className='surface rounded-[24px] p-2 sm:rounded-[28px] lg:col-span-5'>
                        <div className='flex items-center justify-between px-4 pb-3 pt-3'>
                            <span className='eyebrow text-stone'>
                                Arrivals
                            </span>

                            <span className='eyebrow flex items-center gap-2 text-ink-soft'>
                                <LiveDot />
                                {`${total} customers`}
                            </span>
                        </div>

                        <div
                            ref={listRef}
                            role='tablist'
                            aria-label='Customer stories'
                            aria-orientation='vertical'
                            className='no-scrollbar relative flex gap-1.5 overflow-x-auto pb-1 lg:flex-col lg:gap-1 lg:overflow-visible lg:pb-0'
                        >
                            {customerStories.map((item, index) => {
                                const selected = index === active

                                return (
                                    <button
                                        key={item.company}
                                        ref={element => { tabRefs.current[index] = element }}
                                        id={`story-tab-${index}`}
                                        type='button'
                                        role='tab'
                                        aria-selected={selected}
                                        aria-controls='story-panel'
                                        tabIndex={selected ? 0 : -1}
                                        onClick={() => select(index)}
                                        onKeyDown={event => handleKeyDown(event, index)}
                                        className={cn(
                                            'relative flex min-w-[240px] shrink-0 items-center gap-3.5 sm:gap-4 overflow-hidden rounded-2xl px-4 py-4 text-left transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ultramarine lg:min-w-0',
                                            selected ? 'bg-ink text-white' : 'text-ink hover:bg-ink/[0.04]'
                                        )}
                                    >
                                        <span className={cn('font-mono text-[12px]', selected ? 'text-white/45' : 'text-stone')}>
                                            {`0${index + 1}`}
                                        </span>

                                        <span className='min-w-0 flex-1'>
                                            <span className='block truncate font-heading text-[16px] font-semibold tracking-[-0.02em]'>
                                                {item.company}
                                            </span>

                                            <span className={cn('mt-1 block truncate font-mono text-[11px] uppercase tracking-[0.12em]', selected ? 'text-white/55' : 'text-stone')}>
                                                {`${item.lane} · ${item.industry}`}
                                            </span>
                                        </span>

                                        <span className={cn('shrink-0 font-heading text-[15px] font-semibold tabular-nums tracking-[-0.02em]', selected ? 'text-peach' : 'text-ink-soft')}>
                                            {item.stat.value}
                                        </span>

                                        {selected && !reduced && (
                                            <span
                                                key={`${active}-${cycle}`}
                                                aria-hidden='true'
                                                className='absolute inset-x-4 bottom-1.5 h-[2px] origin-left rounded-full bg-peach'
                                                style={{
                                                    animation: `quayside-progress ${STORY_DURATION}ms linear forwards`,
                                                    animationPlayState: running ? 'running' : 'paused'
                                                }}
                                                onAnimationEnd={() => setActive(current => (current + 1) % total)}
                                            />
                                        )}
                                    </button>
                                )
                            })}
                        </div>
                    </Reveal>

                    <Reveal
                        delay={120}
                        className='relative isolate flex min-h-[460px] flex-col overflow-hidden rounded-[24px] surface p-6 sm:min-h-[480px] sm:rounded-[28px] sm:p-10 lg:col-span-7 lg:p-12'
                    >
                        <div aria-hidden='true' className='pointer-events-none absolute -right-28 -top-28 -z-10 size-[26rem] rounded-full bg-[radial-gradient(closest-side,rgba(255,184,156,0.42),transparent)]' />
                        <div aria-hidden='true' className='pointer-events-none absolute -bottom-32 -left-24 -z-10 size-[24rem] rounded-full bg-[radial-gradient(closest-side,rgba(201,187,255,0.32),transparent)]' />

                        <figure id='story-panel' role='tabpanel' aria-labelledby={`story-tab-${active}`} className='flex flex-1 flex-col'>
                            <div className='flex items-center justify-between gap-4'>
                                <span key={`mark-${active}`} className='animate-fade-soft text-[20px] text-ink/80 sm:text-[22px]'>
                                    <PartnerWordmark name={story.company} />
                                </span>

                                <span className='eyebrow hidden text-stone sm:inline'>
                                    {story.lane}
                                </span>
                            </div>

                            <blockquote key={`quote-${active}`} className='mt-7 animate-fade-soft sm:mt-10'>
                                <p className='font-heading text-[clamp(1.55rem,2.6vw,2.3rem)] font-medium leading-[1.2] tracking-[-0.03em] text-ink [text-wrap:pretty]'>
                                    {`“${story.quote}”`}
                                </p>
                            </blockquote>

                            <figcaption className='mt-auto flex flex-col gap-8 pt-10 sm:flex-row sm:items-end sm:justify-between'>
                                <span key={`person-${active}`} className='flex animate-fade-soft items-center gap-3.5'>
                                    <span aria-hidden='true' className='grid size-12 shrink-0 place-items-center rounded-full bg-ink text-[13px] font-semibold text-white'>
                                        {initials(story.name)}
                                    </span>

                                    <span>
                                        <span className='block text-[15px] font-semibold text-ink'>
                                            {story.name}
                                        </span>

                                        <span className='block text-[14px] text-stone'>
                                            {`${story.role}, ${story.company}`}
                                        </span>
                                    </span>
                                </span>

                                <span className='block sm:text-right'>
                                    <span className='headline block text-[clamp(3rem,5vw,4.25rem)] leading-none text-ultramarine'>
                                        <RollingNumber value={story.stat.value} active={inView} duration={1300} />
                                    </span>

                                    <span key={`stat-${active}`} className='mt-2 block max-w-[15rem] animate-fade-soft text-[14px] leading-snug text-ink-soft sm:ml-auto'>
                                        {story.stat.label}
                                    </span>
                                </span>
                            </figcaption>
                        </figure>
                    </Reveal>
                </div>
            </div>
        </CreamSheet>
    )
}
